import { FC, useCallback, MouseEvent } from 'react';
import { Flex } from 'components/Flex';
import { ShiftStatusBadge } from 'components/ShiftStatusBadge';
import { JaneAvatar } from 'components/BKJJaneAvatar';
import { JaneName } from 'components/JaneName';
import { parseVerboseDate } from 'utils';
import { useTranslation } from 'hooks/useTranslation';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import {
  ShiftConflictCardWrapper,
  ShiftSectionA,
  ShiftID,
  PositionText,
  ShiftDate,
  ReleaseShiftLink,
} from 'modals/ShiftModal/src/views/AwardTeamMemberView/ShiftConflictView/ShiftConflictCard.styled';
import { Order, OrderStatusEnum } from 'types/graphql';
import { BiddableShiftStatus } from 'components/Tags/Tags.types';
import { localPersistenceService } from 'services/localPersistenceService';
import { SSF_GLOBAL_CACHE_KEY } from 'services/ssfPersistenceService';
import { useSelector } from 'react-redux';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';

interface ShiftConflictCardProps {
  conflictedShift: Order;
  janeName?: string;
  janeAvatarUrl?: string;
  positionName?: string;
  communityName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conflict?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  status?: any;
  biddingStatus?: Nullable<BiddableShiftStatus>;
  isJaneReleaseAllowed: boolean;
  communityId: string;
}

export const ShiftConflictCard: FC<ShiftConflictCardProps> = ({
  conflictedShift,
  janeName,
  janeAvatarUrl,
  positionName,
  communityName,
  conflict,
  status,
  biddingStatus,
  isJaneReleaseAllowed,
  communityId,
}) => {
  const { t, i18n } = useTranslation();
  const cacheJson = localPersistenceService.getItem(SSF_GLOBAL_CACHE_KEY()) || '{}';
  const parsedJson = JSON.parse(cacheJson);
  const userCommunityIds = parsedJson.locationFilter.map((c: { id: string }) => c.id);
  const timeZone = conflictedShift.timeZone?.toString();
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);

  const handleReleaseShift = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      const params: Record<string, string | number> = {
        conflictedShiftId: conflictedShift.displayId,
      };
      shiftModalRoutingService.goToView('ReleaseShiftView', params);
    },
    [conflictedShift.displayId],
  );

  const handleCardClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      shiftModalRoutingService.open({ shiftModalShiftId: conflictedShift.relayId }, 'DetailView');
    },
    [conflictedShift.relayId],
  );

  const startTime = conflictedShift.orderSchedule?.startTime;
  const endTime = conflictedShift.orderSchedule?.endTime;
  const shiftId = conflictedShift.orderSchedule?.id;

  return (
    <ShiftConflictCardWrapper onClick={handleCardClick}>
      <ShiftSectionA>
        <ShiftStatusBadge status={status?.value}>
          {getTranslatedResponse(status?.value, 'status', i18n, t)}
        </ShiftStatusBadge>
        <ShiftID>
          {t('dashboard:SHIFT_NUMBER')}
          {shiftId}
        </ShiftID>
      </ShiftSectionA>
      <Flex flexDirection="column" flex="1">
        <Flex gap="8px" alignItems="center">
          <JaneAvatar
            size={24}
            status={status?.value}
            url={janeAvatarUrl}
            pendingSignup={conflict?.pendingSignup}
          />
          {janeName && (
            <Flex alignItems="center">
              <JaneName
                mainJane={
                  janeName
                    ? ({
                        firstName: janeName.split(' ')[0] || '',
                        lastName: janeName.split(' ').slice(1).join(' ') || '',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      } as any)
                    : null
                }
                status={status?.name}
              />
            </Flex>
          )}
        </Flex>
        {positionName && <PositionText>{positionName}</PositionText>}
        {startTime && endTime && (
          <ShiftDate>
            {parseVerboseDate([startTime, endTime], timeZone, is24hTimeFormat)}
            {communityName && ` • ${communityName}`}
          </ShiftDate>
        )}
        {status?.value !== OrderStatusEnum.in_progress &&
          isJaneReleaseAllowed &&
          userCommunityIds.includes(communityId) && (
            <ReleaseShiftLink onClick={handleReleaseShift}>
              {t('schedule:RELEASE_SHIFT')}
            </ReleaseShiftLink>
          )}
      </Flex>
    </ShiftConflictCardWrapper>
  );
};
